import { useEffect, useRef } from 'react';
import { create } from 'zustand';

import { SIGNAL_R_STATUS } from '~/common/constants';
import { CONDITION_CATEGORY } from '~/common/constants/api-ids';

type NotificationType = (typeof SIGNAL_R_STATUS)[keyof typeof SIGNAL_R_STATUS];
type conditionCategory =
  (typeof CONDITION_CATEGORY)[keyof typeof CONDITION_CATEGORY];

export type NotificationMessage =
  | {
      notificationType: NotificationType;
      conditionCategory: conditionCategory;
    }
  | NotificationType;

export type Notification = {
  notificationType: NotificationType;
  correlationId?: number;
  receivedDateTime?: string;
  conditionCategory?: conditionCategory;
};

type EventListenerCallback = (message: Notification) => void;

type EventListenersContext = {
  map: Map<
    string,
    {
      messages: NotificationType[];
      callback: EventListenerCallback;
    }
  >;
  register: (
    registrationId: string,
    validMessages: NotificationType[],
    cb: EventListenerCallback,
  ) => void;
  unregister: (registrationId: string) => void;
};

type SignalRStore = {
  setLatestSignalRNotification: (notification: Notification) => void;
  setConditionCategoryToWaitFor: (
    conditionCategory: conditionCategory | null,
  ) => void;
  setIsConnected: (isConnected: boolean) => void;
  latestSignalRNotification?: Notification;
  conditionCategoryToWaitFor?: conditionCategory | null;
  isConnected: boolean;
  eventListeners: EventListenersContext;
};

export const useSignalRStore = create<SignalRStore>()((set, get) => ({
  conditionCategoryToWaitFor: null,
  isConnected: false,
  setIsConnected: (isConnected) => set({ isConnected }),
  setLatestSignalRNotification: (notification) => {
    set({ latestSignalRNotification: notification });

    const listeners = get().eventListeners.map;

    listeners.forEach((value) => {
      if (value.messages.includes(notification.notificationType)) {
        value.callback(notification);
      }
    });
  },
  setConditionCategoryToWaitFor: (conditionCategory) =>
    set({ conditionCategoryToWaitFor: conditionCategory }),
  eventListeners: {
    map: new Map(),
    register: (registrationId, validMessages, cb) => {
      const newMap = new Map(get().eventListeners.map);
      newMap.set(registrationId, { messages: validMessages, callback: cb });

      set({
        eventListeners: {
          ...get().eventListeners,
          map: newMap,
        },
      });
    },
    unregister: (registrationId) => {
      const newMap = new Map(get().eventListeners.map);

      newMap.delete(registrationId);

      set({
        eventListeners: {
          ...get().eventListeners,
          map: newMap,
        },
      });
    },
  },
}));

export function useSignalRIsConnected() {
  return useSignalRStore((state) => state.isConnected);
}

export function useLatestSignalRNotification() {
  return useSignalRStore((state) => {
    return state.latestSignalRNotification;
  });
}

type useSignalRMessageEventOptions = {
  /** A notification type or list of them that the hook should listen for */
  notifications: NotificationType[] | NotificationType;
  onMessage: (message: Notification) => void;
  /** When false the onMessage callback will not be called */
  isEnabled?: boolean;
};

/**
 * Registers a SignalR message event listener that calls the onMessage callback when a notification is received.
 *
 * @example
 * useSignalRRealtime({
 *   notifications: SIGNAL_R_STATUS.CLASSIFY_STATUS_CHANGE, // Can also be an array
 *   onMessage: (notification) => {
 *     // Handle the notification
 *   },
 *   isEnabled: isLoadingSomething, // Optional, defaults to true
 * });
 */
export function useSignalRRealtime(options: useSignalRMessageEventOptions) {
  const { notifications: messages, onMessage, isEnabled } = options;

  const context = useSignalRStore((s) => s.eventListeners);

  // eslint-disable-next-line @eslint-react/purity
  const registrationId = useRef(crypto.randomUUID()).current;

  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (isEnabled === false) {
      context.unregister(registrationId);
      return;
    }

    context.register(
      registrationId,
      Array.isArray(messages) ? messages : [messages],
      (notification) => {
        onMessageRef.current(notification);
      },
    );

    return () => {
      context.unregister(registrationId);
    };

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [JSON.stringify(messages), isEnabled]);
}
